import { Link } from "react-router-dom";
import { Users, Music, SquareParking, Handshake } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const Divider = () => <div className="h-px w-16 mx-auto bg-[#c9a24b]/25" aria-hidden="true" />;

const Course = ({ label, value, trad, testid }) => (
    <div className="text-center" data-testid={testid}>
        <h3 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{label}</h3>
        <p className="font-display font-light text-xl sm:text-2xl text-[#efe6d6]/90 mt-2 leading-snug">{value}</p>
        {trad ? <p className="font-display italic font-light text-sm text-[#efe6d6]/50 mt-1">{trad}</p> : null}
    </div>
);

export const Experiencia = () => {
    const { t } = useLanguage();
    const m = t.experiencia.menuOficial;

    const notas = [
        { id: "bilhetes", icon: Users, text: m.notaBilhetes },
        { id: "silencio", icon: Music, text: m.notaSilencio },
        { id: "estacionamento", icon: SquareParking, text: m.notaEstacionamento },
        { id: "rececao", icon: Handshake, text: m.notaRececao },
    ];

    return (
        <section id="experiencia" className="py-24 sm:py-36" data-testid="experiencia-section">
            <div className="mx-auto max-w-[1200px] px-5 sm:px-10">
                <Reveal className="text-center">
                    <p className="font-caps text-[11px] tracking-[0.35em] text-[#c9a24b] uppercase">{t.experiencia.menuLabel}</p>
                    <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#efe6d6] mt-6">
                        {t.experiencia.tituloA}<span className="gold-text">{t.experiencia.tituloGold}</span>{t.experiencia.tituloB}<br />
                        {t.experiencia.tituloC}
                    </h2>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="mt-12 grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <p className="text-base sm:text-lg font-light text-[#efe6d6]/70 leading-relaxed">
                            {t.experiencia.p1}
                        </p>
                        <p className="text-base sm:text-lg font-light text-[#efe6d6]/70 leading-relaxed">
                            {t.experiencia.p2}
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="portrait-frame max-w-xl sm:max-w-2xl mx-auto mt-14 bg-[#0a0705]/80 px-6 sm:px-10 py-10 sm:py-12" data-testid="menu-card">
                        <div className="flex justify-center">
                            <img loading="lazy" decoding="async" src="/images/ornamento-oficial.webp" alt="" aria-hidden="true" className="w-40 opacity-90" />
                        </div>

                        <div className="mt-6 border border-[#c9a24b]/35 px-6 py-4 text-center" data-testid="menu-bebidas">
                            <p className="font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b]">{m.bebidasTitulo}</p>
                            <p className="text-sm font-light text-[#efe6d6]/70 mt-1">{m.bebidasTexto}</p>
                        </div>

                        <div className="mt-9 space-y-8">
                            <div className="text-center" data-testid="menu-entradas">
                                <h3 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{m.entradas}</h3>
                            </div>
                            <Divider />
                            <Course label={m.sopa} value={m.sopaValor} trad={m.sopaTrad} testid="menu-sopa" />
                            <Divider />
                            <div data-testid="menu-prato">
                                <h3 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b] text-center">{m.prato}</h3>
                                <div className="mt-5 space-y-6">
                                    <div className="text-center" data-testid="menu-peixe">
                                        <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/55">{m.peixeLabel}</p>
                                        <p className="font-display font-light text-xl sm:text-2xl text-[#efe6d6]/90 mt-1.5 leading-snug">{m.peixeValor}</p>
                                        {m.peixeTrad ? <p className="font-display italic font-light text-sm text-[#efe6d6]/50 mt-1">{m.peixeTrad}</p> : null}
                                    </div>
                                    <div className="text-center" data-testid="menu-carne">
                                        <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/55">{m.carneLabel}</p>
                                        <p className="font-display font-light text-xl sm:text-2xl text-[#efe6d6]/90 mt-1.5 leading-snug">{m.carneValor}</p>
                                        {m.carneTrad ? <p className="font-display italic font-light text-sm text-[#efe6d6]/50 mt-1">{m.carneTrad}</p> : null}
                                    </div>
                                    <p className="text-center font-display italic font-light text-sm text-[#c9a24b]/80" data-testid="menu-acompanhamento">{m.acompanhamento}</p>
                                </div>
                            </div>
                            <Divider />
                            <div className="text-center" data-testid="menu-sobremesa">
                                <h3 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{m.sobremesa}</h3>
                                <p className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#efe6d6]/55 mt-2">{m.sobremesaNota}</p>
                                <p className="font-display font-light text-xl sm:text-2xl text-[#efe6d6]/90 mt-2 leading-snug">{m.sobremesaOpcoes}</p>
                            </div>
                            <Divider />
                            <Course label={m.final} value={m.finalValor} testid="menu-final" />
                        </div>

                        <div className="mt-10 pt-8 border-t border-[#c9a24b]/20 grid sm:grid-cols-2 gap-x-8 gap-y-6">
                            {notas.map((n) => (
                                <div key={n.id} className="flex gap-3 items-start" data-testid={`menu-nota-${n.id}`}>
                                    <n.icon size={16} strokeWidth={1.4} className="text-[#c9a24b] shrink-0 mt-0.5" />
                                    <p className="text-xs font-light text-[#efe6d6]/60 leading-relaxed">{n.text}</p>
                                </div>
                            ))}
                        </div>

                        <p className="mt-8 text-center font-display italic font-light text-base text-[#c9a24b]/90 leading-relaxed" data-testid="menu-fecho">
                            {m.fecho}
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.25} className="text-center">
                    <p className="mt-8 text-sm sm:text-base font-light text-[#efe6d6]/70 leading-relaxed max-w-xl mx-auto" data-testid="menu-confirmacao">
                        <span className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] block mb-2">{t.experiencia.importante}</span>
                        {t.experiencia.notaA}
                        <span className="text-[#ecd49b]">{t.experiencia.prazoData}</span>.
                    </p>
                    <Link
                        to="/confirmar"
                        data-testid="confirmar-presenca-button"
                        className="inline-block mt-6 font-caps text-xs sm:text-sm tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-8 sm:px-10 py-3.5 sm:py-4 hover:bg-[#ecd49b] transition-colors duration-400"
                    >
                        {t.experiencia.confirmar}
                    </Link>
                </Reveal>
            </div>
        </section>
    );
};
